<template>
  <div class="max-w-5xl mx-auto pb-10 relative">

    <div
      class="space-y-6 transition-all duration-300 animate-fade-in"
      :class="{ 'blur-sm pointer-events-none opacity-50': showModal }"
    >

      <div class="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 class="text-2xl font-bold text-slate-900 dark:text-white tracking-tight">Unidades de Medida</h1>
          <p class="text-slate-500 dark:text-slate-400 text-sm">Define cómo se cuantifican tus productos.</p>
        </div>
        <button
          @click="openCreate"
          class="flex items-center gap-2 px-5 py-2.5 bg-brand-600 hover:bg-brand-700 text-white font-bold rounded-xl shadow-lg shadow-brand-500/30 transition-all active:scale-95"
        >
          <Plus class="w-5 h-5" /> Nueva Unidad
        </button>
      </div>

      <div class="bg-white dark:bg-slate-900 p-4 rounded-2xl border border-slate-200 dark:border-slate-800 shadow-sm flex items-center gap-4">
        <div class="relative flex-1 max-w-md">
          <Search class="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <input
            v-model="searchTerm"
            type="text"
            placeholder="Buscar unidad..."
            class="w-full pl-10 pr-4 py-2 bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-brand-500/20 focus:border-brand-500 transition-all"
          >
        </div>
      </div>

      <div class="bg-white dark:bg-slate-900 rounded-2xl border border-slate-200 dark:border-slate-800 shadow-sm overflow-hidden">
        <div class="overflow-x-auto">
          <table class="w-full text-sm text-left">
            <thead class="text-xs text-slate-500 uppercase bg-slate-50 dark:bg-slate-800/50 border-b border-slate-100 dark:border-slate-800">
              <tr>
                <th class="px-6 py-4 font-semibold">Nombre</th>
                <th class="px-6 py-4 font-semibold">Abreviatura / Código</th>
                <th class="px-6 py-4 font-semibold text-right">Acciones</th>
              </tr>
            </thead>
            <tbody class="divide-y divide-slate-100 dark:divide-slate-800">
              <tr v-if="isLoading">
                <td colspan="3" class="px-6 py-8 text-center text-slate-500">Cargando...</td>
              </tr>
              <tr v-else-if="filteredList.length === 0">
                <td colspan="3" class="px-6 py-8 text-center text-slate-500">No hay unidades registradas.</td>
              </tr>
              <tr v-else v-for="item in filteredList" :key="item.id" class="hover:bg-slate-50 dark:hover:bg-slate-800/50 transition-colors group">

                <td class="px-6 py-4 font-medium text-slate-900 dark:text-white">
                  {{ item.name }}
                </td>

                <td class="px-6 py-4">
                  <span class="font-mono text-xs bg-slate-100 dark:bg-slate-800 px-2 py-1 rounded text-slate-600 dark:text-slate-300">
                    {{ item.code }}
                  </span>
                </td>

                <td class="px-6 py-4 text-right">
                  <div class="flex justify-end gap-2">
                    <button @click="openEdit(item)" class="p-2 text-slate-400 hover:text-brand-600 hover:bg-brand-50 rounded-lg transition-colors">
                      <Edit class="w-4 h-4" />
                    </button>
                    <button @click="deleteItem(item)" class="p-2 text-slate-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors">
                      <Trash2 class="w-4 h-4" />
                    </button>
                  </div>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>

    <UnitForm
      :show="showModal"
      :unitToEdit="selectedItem"
      @close="showModal = false"
      @saved="loadData"
    />

  </div>
</template>

<script setup>
import { ref, onMounted, computed } from 'vue';
import { Search, Plus, Edit, Trash2 } from 'lucide-vue-next';
import apiClient from '@/api/client';
import { SecureStorage } from '@/utils/secureStorage';
import Swal from 'sweetalert2';
import UnitForm from './Form.vue';
import { useToast } from '@/composables/useToast';

const list = ref([]);
const isLoading = ref(true);
const searchTerm = ref('');
const showModal = ref(false);
const selectedItem = ref(null);
const { showSuccess, showError } = useToast();

const loadData = async () => {
  isLoading.value = true;
  try {
    const user = SecureStorage.getItem('llevo_user');
    if (!user?.company) return;

    const res = await apiClient.get(`/units?company=${user.company}`);
    list.value = Array.isArray(res.data) ? res.data : (res.data.data || []);
  } catch (e) {
    console.error(e);
  } finally {
    isLoading.value = false;
  }
};

onMounted(loadData);

const filteredList = computed(() => {
  if (!searchTerm.value) return list.value;
  const term = searchTerm.value.toLowerCase();
  return list.value.filter(i =>
    i.name.toLowerCase().includes(term) ||
    i.code.toLowerCase().includes(term)
  );
});

const openCreate = () => { selectedItem.value = null; showModal.value = true; };
const openEdit = (item) => { selectedItem.value = item; showModal.value = true; };

const deleteItem = (item) => {
  Swal.fire({
    title: '¿Eliminar unidad?',
    text: "No podrás eliminarla si hay productos que la usan.",
    icon: 'warning',
    showCancelButton: true,
    confirmButtonColor: '#ef4444',
    confirmButtonText: 'Sí, eliminar',
    cancelButtonText: 'Cancelar'
  }).then(async (res) => {
    if (res.isConfirmed) {
      try {
        await apiClient.delete(`/units/${item.id}`);
        list.value = list.value.filter(i => i.id !== item.id);
        showSuccess('Unidad eliminada.');
      } catch (e) {
        showError('No se pudo eliminar (está en uso).');
      }
    }
  });
};
</script>

<style scoped>
@keyframes fade-in {
  from { opacity: 0; transform: translateY(5px); }
  to { opacity: 1; transform: translateY(0); }
}
.animate-fade-in {
  animation: fade-in 0.3s ease-out forwards;
}
</style>